/**
 * MentionMenu — the @-mention picker overlay (attach source section/table).
 *
 * Rendering-only sibling of CommandMenu: same absolute bottom-anchored
 * chrome, same keyboard/hover contract (the rail owns the highlight
 * index and the stage state; this component just draws the current
 * stage's list). Three stages:
 *
 *   type  → Section / Table
 *   file  → source documents in the active library
 *   asset → section titles / table captions in the picked file
 *
 * The breadcrumb in the context line shows where the writer is
 * (``@ mention › Table › CSR Study 123``); typing after the ``@``
 * filters the visible list; Esc closes; Backspace on an empty query
 * steps back one stage (handled by the rail).
 */

import { makeStyles } from "@fluentui/react-components";
import React, { forwardRef } from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";
import type { MentionStage } from "../mentions";

export interface MentionMenuEntry {
  key: string;
  label: string;
  /** Secondary text (e.g. the asset's source file) — optional. */
  sublabel?: string;
}

export interface MentionMenuProps {
  stage: MentionStage;
  /** Breadcrumb segments after "@ mention" (picked type, picked file). */
  breadcrumb: string[];
  /** Entries for the CURRENT stage, already query-filtered by the rail. */
  entries: ReadonlyArray<MentionMenuEntry>;
  highlightedIndex: number;
  loading?: boolean;
  error?: string | null;
  /** Non-null when mentions can't work at all (no library selected). */
  disabledHint?: string | null;
  onSelect: (index: number) => void;
  onClose: () => void;
  onHoverItem?: (index: number) => void;
  /** Inline overrides for anchoring the card to the "@" column. */
  style?: React.CSSProperties;
}

const useStyles = makeStyles({
  root: {
    position: "absolute",
    bottom: "100%",
    zIndex: 6,
    minWidth: "260px",
    maxWidth: "320px",
    background: "var(--surface-raised)",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "6px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
    marginBottom: "6px",
    fontFamily: fonts.body,
  },
  contextLine: {
    padding: "5px 8px 5px 10px",
    fontSize: "10px",
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.mono,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  breadcrumb: {
    color: brandColors.primary,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  spacer: {
    flex: 1,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    cursor: "pointer",
    padding: "0 4px",
    "&:hover": {
      color: inkColors.ink2,
    },
  },
  list: {
    display: "flex",
    flexDirection: "column",
    padding: "6px 8px",
    maxHeight: "220px",
    overflowY: "auto",
  },
  stateLine: {
    padding: "12px 16px",
    fontSize: "11px",
    color: inkColors.ink4,
    fontStyle: "italic",
    textAlign: "center",
  },
  errorLine: {
    padding: "10px 16px",
    fontSize: "10.5px",
    color: "var(--danger)",
  },
  item: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    padding: "4px 6px",
    fontSize: "10.5px",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    borderRadius: "3px",
    "&:hover": {
      background: surfaceColors.paperDim,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-1px",
    },
  },
  itemHighlighted: {
    background: brandColors.light,
  },
  itemLabel: {
    flex: 1,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemSublabel: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    flexShrink: 0,
    maxWidth: "40%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

export const MentionMenu = forwardRef<HTMLDivElement, MentionMenuProps>(function MentionMenu(
  { breadcrumb, entries, highlightedIndex, loading, error, disabledHint, onSelect, onClose, onHoverItem, style },
  ref,
) {
  const styles = useStyles();
  // Compact header: just where the writer is (the picked type/file), or a
  // faint "@ mention" label at the first stage. Hints/Esc chrome dropped.
  const crumb = breadcrumb.length > 0 ? breadcrumb.join(" › ") : "@ mention";
  return (
    <div ref={ref} className={styles.root} style={style} role="menu" aria-label="Mention picker">
      <div className={styles.contextLine}>
        <span className={styles.breadcrumb}>{crumb}</span>
        <span className={styles.spacer} />
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close mention picker">
          ×
        </button>
      </div>
      <div className={styles.list}>
        {disabledHint && <output className={styles.stateLine}>{disabledHint}</output>}
        {!disabledHint && loading && <output className={styles.stateLine}>Loading…</output>}
        {!disabledHint && !loading && error && (
          <output className={styles.errorLine}>{error}</output>
        )}
        {!disabledHint && !loading && !error && entries.length === 0 && (
          <output className={styles.stateLine}>Nothing matches. Try a different term or press Esc.</output>
        )}
        {!disabledHint &&
          !loading &&
          !error &&
          entries.map((entry, idx) => {
            const className = [styles.item, idx === highlightedIndex ? styles.itemHighlighted : ""]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                key={entry.key}
                type="button"
                role="menuitem"
                className={className}
                onClick={() => onSelect(idx)}
                onMouseEnter={onHoverItem ? () => onHoverItem(idx) : undefined}
                onMouseDown={(e) => {
                  e.preventDefault(); /* keep focus on input */
                }}
                aria-selected={idx === highlightedIndex || undefined}
                title={entry.sublabel ? `${entry.label} — ${entry.sublabel}` : entry.label}
              >
                <span className={styles.itemLabel}>{entry.label}</span>
                {entry.sublabel && <span className={styles.itemSublabel}>{entry.sublabel}</span>}
              </button>
            );
          })}
      </div>
    </div>
  );
});

export default MentionMenu;
